import React from "react";
import Cookies from "js-cookie";
import { cn } from "@/lib/utils";
import { SendIcon, StopCircle, ImageIcon } from "lucide-react";
import { type UseFormReturn, type FieldValues } from "react-hook-form";
import { useSetAtom } from "jotai";
import { AutosizeTextarea, AutosizeTextAreaRef } from "@/components/ui/autosize-textarea";
import { ModelSelectionDropdown } from "@/components/chat/ModelSelectionDropdown";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { imageModels, textModels } from "@/lib/aiproviders/models";
import { proDialog } from "@/state/dialogs";
import { useUser } from "@/hooks/useUser";
import { COOKIE_NAMES, COOKIE_EXPIRY_DAYS } from "@/lib/constants/cookies";

type AITextareaFormData = {
  message: string;
  textModel: string;
  imageModel: string;
  imageMode: boolean;
};

interface AITextareaProps<TFieldValues extends FieldValues = AITextareaFormData> {
  form: UseFormReturn<TFieldValues>;
  onSubmit?: (data: TFieldValues) => void;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onValueChange?: (value: string) => void;
  onStop?: () => void;
  imageMode?: boolean;
  onImageModeChange?: (imageMode: boolean) => void;
}

export interface AITextareaRef {
  focus: () => void;
}

export const AITextarea = React.forwardRef<AITextareaRef, AITextareaProps>(
  (
    {
      form,
      onSubmit,
      placeholder = "What would you like to know?",
      className,
      isLoading = false,
      onFocus,
      onBlur,
      onValueChange,
      onStop,
      imageMode = false,
      onImageModeChange,
    },
    ref,
  ) => {
    const autosizeRef = React.useRef<AutosizeTextAreaRef | null>(null);
    const { hasPro } = useUser();
    const setProDialogOpen = useSetAtom(proDialog);

    // Watch form values for display only
    const message = form.watch("message");
    const model = form.watch("model");

    const handleModelChange = React.useCallback(
      (newModel: string) => {
        form.setValue("model", newModel as any);
        // Set the appropriate cookie based on current mode
        if (imageMode) {
          Cookies.set(COOKIE_NAMES.SELECTED_IMAGE_MODEL, newModel, { expires: COOKIE_EXPIRY_DAYS });
        } else {
          Cookies.set(COOKIE_NAMES.SELECTED_TEXT_MODEL, newModel, { expires: COOKIE_EXPIRY_DAYS });
        }
      },
      [form, imageMode],
    );

    const handleImageModeToggle = React.useCallback(() => {
      const newImageMode = !imageMode;

      // Check if user has Pro when enabling image mode
      if (newImageMode && !hasPro) {
        setProDialogOpen(true);
        return;
      }

      onImageModeChange?.(newImageMode);

      // Switch to appropriate default model when toggling image mode
      const newModels = newImageMode ? imageModels : textModels;
      const currentModelValid = newModels.some((m) => m.id === model);

      if (!currentModelValid && newModels.length > 0) {
        const defaultModel = newModels[0].id;
        form.setValue("model", defaultModel as any);
        // Set the appropriate cookie for the new mode
        if (newImageMode) {
          Cookies.set(COOKIE_NAMES.SELECTED_IMAGE_MODEL, defaultModel, {
            expires: COOKIE_EXPIRY_DAYS,
          });
        } else {
          Cookies.set(COOKIE_NAMES.SELECTED_TEXT_MODEL, defaultModel, {
            expires: COOKIE_EXPIRY_DAYS,
          });
        }
      }
    }, [imageMode, onImageModeChange, model, form, hasPro, setProDialogOpen]);

    // Filter models based on image mode
    const filteredModels = React.useMemo(() => {
      return imageMode ? imageModels : textModels;
    }, [imageMode]);

    const handleSubmit = React.useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        form.handleSubmit((data) => {
          onSubmit?.(data);
        })(event);
      },
      [form, onSubmit],
    );

    React.useImperativeHandle(ref, () => ({
      focus: () => {
        // Directly focus the textarea element
        const textArea = autosizeRef.current?.textArea;
        if (textArea) {
          textArea.focus();
          // Set cursor position to the end of the text
          const length = textArea.value.length;
          textArea.setSelectionRange(length, length);
        }
      },
    }));

    return (
      <form
        className={cn(
          "w-full overflow-hidden rounded-xl border bg-background shadow-sm select-text",
          className,
        )}
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const messageValue = form.getValues("message");
            if (
              messageValue &&
              typeof messageValue === "string" &&
              messageValue.trim() &&
              !isLoading
            ) {
              form.handleSubmit((data) => {
                onSubmit?.(data);
              })();
            }
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            if (isLoading && onStop) {
              onStop();
            } else {
              autosizeRef.current?.textArea?.blur();
            }
            return;
          }
        }}
      >
        <div className="relative border-b border-border">
          <AutosizeTextarea
            ref={autosizeRef}
            placeholder={imageMode ? "Describe the image you want to generate..." : placeholder}
            minHeight={52}
            maxHeight={200}
            className="resize-none rounded-none border-none p-3 shadow-none outline-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 select-text"
            tabIndex={0}
            value={message || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              form.setValue("message", newValue);
              // Call the parent's onValueChange to persist to Jotai atoms
              onValueChange?.(newValue);
            }}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          {form.formState.errors.message && (
            <div className="absolute top-full left-0 mt-1 text-sm text-destructive">
              {form.formState.errors.message?.message as string}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-1">
          <div className="flex gap-2">
            <ModelSelectionDropdown
              currentModel={model}
              onSelect={handleModelChange}
              isImageMode={imageMode}
              side="top"
              align="start"
              trigger={
                <Button
                  variant="ghost"
                  className="h-auto p-2 border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors hover:bg-accent hover:text-foreground justify-start"
                >
                  {filteredModels.find((m) => m.id === model)?.name ||
                    filteredModels[0]?.name ||
                    "Select model"}
                </Button>
              }
            />

            <Toggle
              pressed={imageMode}
              onPressedChange={handleImageModeToggle}
              variant="outline"
              className={cn(
                "gap-1.5 transition-all border-transparent",
                imageMode ? "!text-primary" : "text-muted-foreground",
              )}
              aria-label="Toggle image mode"
            >
              <ImageIcon className="size-4" />
              <span className="text-xs font-medium">Image</span>
            </Toggle>
          </div>

          <Button
            type={isLoading ? "button" : "submit"}
            size="sm"
            className="gap-1.5 rounded-lg rounded-br-xl"
            disabled={!isLoading && (!message || typeof message !== "string" || !message.trim())}
            onClick={isLoading ? onStop : undefined}
          >
            {isLoading ? (
              <>
                <StopCircle className="size-4" />
                <span className="text-xs">Stop</span>
              </>
            ) : (
              <SendIcon className="size-4" />
            )}
          </Button>
        </div>
      </form>
    );
  },
);

AITextarea.displayName = "AITextarea";

// Export the form data type for convenience
export type { AITextareaFormData };
